import { useMemo } from 'react';
import { ClaimWithActions } from '../types';

type ParsedDocument = {
  title: string;
  bodyContent: BodyContentItem[];
  unmatchedClaims: ClaimWithActions[];
};

type BodyContentItem = {
  type: 'heading' | 'paragraph';
  parts: ContentPart[];
};

type ContentPart = string | ClaimWithActions;

/**
 * Parses GROBID XML (a structured academic paper format) to extract the document title
 * and body content (headings and paragraphs), then inserts claim objects at positions
 * where their text appears in the document. Returns a parsed document structure with
 * claims embedded inline within the text content.
 */
export const useGrobidXml = (
  grobidXml: string,
  claims: ClaimWithActions[],
): ParsedDocument => {
  const parsedDocument = useMemo(() => {
    return (
      parseGrobidXml(grobidXml) || {
        title: '',
        bodyContent: [],
        unmatchedClaims: [],
      }
    );
  }, [grobidXml]);

  const { bodyContent: bodyContentWithClaims, matchedClaims } = useMemo(() => {
    return insertClaimsIntoBodyContent(
      parsedDocument?.bodyContent || [],
      claims,
    );
  }, [parsedDocument, claims]);

  const unmatchedClaims = useMemo(() => {
    return claims.filter((claim) => !matchedClaims.has(claim));
  }, [claims, matchedClaims]);

  return {
    title: parsedDocument.title,
    bodyContent: bodyContentWithClaims,
    unmatchedClaims,
  };
};

const insertClaimsIntoBodyContent = (
  bodyContent: BodyContentItem[],
  claims: ClaimWithActions[],
): { bodyContent: BodyContentItem[]; matchedClaims: Set<ClaimWithActions> } => {
  const matchedClaims = new Set<ClaimWithActions>();

  const modifiedBodyContent = bodyContent.map((item) => {
    // duplicates the items so we can modify the parts without affecting the original
    const newItem = { ...item };
    newItem.parts = item.parts.flatMap((part) => {
      // in practice there should only be strings at this point
      if (typeof part === 'string') {
        // find the offset of the claim inside this part if it exists
        const claimMatches = claims.map((claim) =>
          matchClaimText(part, claim.text),
        );

        const claimsWithMatches = claims.flatMap((claim, index) => {
          const match = claimMatches[index];
          if (match !== null) {
            matchedClaims.add(claim);
            return [{ claim, match }];
          } else {
            return [];
          }
        });

        // sort the claims by the offset so we can keep track of offset as we modify the parts
        claimsWithMatches.sort((a, b) => a.match.index - b.match.index);

        // keep track of how much of the part hasn't been processed yet
        let remainingPart = part;
        let offset = 0;
        return claimsWithMatches
          .flatMap(({ claim, match }): ContentPart[] => {
            // because we're modifying the part, we need to adjust for an index into the remaining part
            const remainingIndex = match.index - offset;

            // insert the claim at it's index
            const newParts = [remainingPart.slice(0, remainingIndex), claim];

            // update the remaining part to the part after the claim
            remainingPart = remainingPart.slice(remainingIndex + match.length);
            offset += remainingIndex + match.length;

            return newParts;
          })
          .concat([remainingPart as ContentPart]);
      } else {
        return [part];
      }
    });
    return newItem;
  });

  return { bodyContent: modifiedBodyContent, matchedClaims };
};

/**
 * Matches claim text within a string, ignoring whitespace differences.
 * Each sequence of whitespace characters in the claim text can match any
 * sequence of whitespace characters (including zero) in the target string.
 *
 * @param part - The string to search within
 * @param claimText - The claim text to search for
 * @returns Object with index and length of match, or null if no match found
 */
const matchClaimText = (
  part: string,
  claimText: string,
): { index: number; length: number } | null => {
  // Escape special regex characters
  const escapedText = claimText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Replace each whitespace sequence with a pattern that matches zero or more whitespace
  // This allows matching even when whitespace is present in one text but not the other
  const pattern = escapedText.replace(/\s+/g, '\\s*');

  // Create regex and search for the pattern
  const regex = new RegExp(pattern);
  const match = part.match(regex);

  if (match && match.index !== undefined) {
    return { index: match.index, length: match[0].length };
  }

  return null;
};

const parseGrobidXml = (grobidXml: string): ParsedDocument | null => {
  if (!grobidXml) {
    return null;
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(grobidXml, 'text/xml');
  const titleElement = xmlDoc.querySelector('titleStmt title');
  const abstractElements = xmlDoc.querySelectorAll(
    'teiHeader profileDesc abstract > *',
  );
  const bodyElements = xmlDoc.querySelectorAll('text body *');

  const bodyContent: BodyContentItem[] = [];

  const title = titleElement?.textContent?.trim() || '';

  if (abstractElements.length > 0) {
    bodyContent.push({ type: 'heading', parts: ['Abstract'] });
    bodyContent.push({
      type: 'paragraph',
      parts: Array.from(abstractElements).map(
        (element) => element.textContent?.trim() || '',
      ),
    });
  }

  bodyContent.push(
    ...Array.from(bodyElements).flatMap((element): BodyContentItem[] => {
      if (element.tagName === 'head') {
        const content = element.textContent?.trim();
        if (content) {
          return [{ type: 'heading', parts: [content] }];
        }
      } else if (element.tagName === 'p') {
        const content = element.textContent?.trim();
        if (content) {
          return [{ type: 'paragraph', parts: [content] }];
        }
      }
      return [];
    }),
  );

  return { title, bodyContent, unmatchedClaims: [] };
};
